/**
 * Theme Provider
 *
 * Provides Fluent UI theming with Veracity brand colors.
 */

import { type ReactNode, createContext, useContext, useState, useMemo, useCallback } from 'react';
import { FluentProvider } from '@fluentui/react-components';
import { veracityLightTheme, veracityDarkTheme } from '@shared/ui';

// =============================================================================
// Theme Context
// =============================================================================

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  /** Current theme mode */
  mode: ThemeMode;
  /** Whether dark mode is active */
  isDark: boolean;
  /** Set theme mode */
  setMode: (mode: ThemeMode) => void;
  /** Toggle between light and dark */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Hook to access theme context
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// =============================================================================
// System Theme Detection
// =============================================================================

/**
 * Check if system prefers dark mode
 */
function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Get stored theme preference
 */
function getStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem('veracity-theme-mode');
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'light';
}

/**
 * Store theme preference
 */
function storeTheme(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('veracity-theme-mode', mode);
}

// =============================================================================
// Provider Component
// =============================================================================

interface ThemeProviderProps {
  children: ReactNode;
  /** Default theme mode */
  defaultMode?: ThemeMode;
}

/**
 * Theme Provider component
 * Provides Fluent UI theming with theme switching capability
 */
export function ThemeProvider({ children, defaultMode }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => defaultMode ?? getStoredTheme());

  // Determine if dark mode should be active
  const isDark = useMemo(() => {
    if (mode === 'system') {
      return getSystemPrefersDark();
    }
    return mode === 'dark';
  }, [mode]);

  // Select theme based on mode
  const theme = useMemo(() => {
    return isDark ? veracityDarkTheme : veracityLightTheme;
  }, [isDark]);

  // Set mode with persistence
  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    storeTheme(newMode);
  }, []);

  // Toggle between light and dark
  const toggle = useCallback(() => {
    setModeState((current) => {
      const newMode = current === 'dark' ? 'light' : 'dark';
      storeTheme(newMode);
      return newMode;
    });
  }, []);

  const contextValue: ThemeContextValue = useMemo(
    () => ({
      mode,
      isDark,
      setMode,
      toggle,
    }),
    [mode, isDark, setMode, toggle]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <FluentProvider theme={theme}>
        {children}
      </FluentProvider>
    </ThemeContext.Provider>
  );
}
